import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, OverlayTrigger, Tooltip, Nav } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { DUYET_HOCPHANTIENSI_API, HOCPHANTIENSI_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { trangThaiHocPhanValue, objectTrangThaiHocPhan, objectLoaiHocPhan } from '@app/utils/define';
import { Get, Put, DeleteSingle } from '@app/utils/httpProvider';
import moment from 'moment';
import { toast } from 'react-toastify';
import HocPhanTienSiEdit from './HocPhanTienSiEdit';
import HocPhanTienSiAdd from './HocPhanTienSiAdd';
import ChamDeCuongModal from './ChamDeCuongModal';
import { ConfirmModal } from '@app/modules/modal/ConfirmModal';
import { useMutation, useQueryClient } from 'react-query';

const DuyetHocPhanTienSi = ({ hoSoId, activeTab, setActiveTab }) => {
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [trangThai, setTrangThai] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');
    
    const queryClient = useQueryClient();

    const { isLoading, data: queryData, refetch } = useQuery(
        ['duyetHocPhanTienSiData', hoSoId],
        () => Get(DUYET_HOCPHANTIENSI_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        }
    }

    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const handleAction = (item) => {
        setDataEdit(item);
        setTrangThai(item.trangThai || trangThaiHocPhanValue.choHoc);
        setShowModal(true);
    };

    const deleteFunc = useMutation((id) => DeleteSingle(HOCPHANTIENSI_API.DELETE_ITEM, id, null));

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['duyetHocPhanTienSiData', hoSoId]);
                    queryClient.invalidateQueries(['hocPhanTienSiData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            HoSoNghienCuuSinhId: hoSoId,
            LoaiHocPhan: dataEdit.loaiHocPhan || '',
            TenHocPhan: dataEdit.tenHocPhan || '',
            TuNgay: dataEdit.tuNgay || null,
            DenNgay: dataEdit.denNgay || null,
            TrangThai: trangThai
        };

        setIsSaving(true);
        try {
            const res = await Put(DUYET_HOCPHANTIENSI_API.DUYET_HO_SO, dataEdit.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Lưu thành công!');
                queryClient.invalidateQueries(['hocPhanTienSiData', hoSoId]);
                refetch();
                setShowModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiHocPhanValue.choHoc: return <Badge variant="warning">Chờ học</Badge>;
            case trangThaiHocPhanValue.dangHoc: return <Badge variant="info">Đang học</Badge>;
            case trangThaiHocPhanValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    const getLoaiHocPhanLabel = (val) => {
        const item = objectLoaiHocPhan.find(x => x.value === val);
        return item ? item.label : val;
    };

    return (
        <>
        <Card className="h-100 card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="hocPhanBoSung" className="font-weight-bold">Học phần bổ sung</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="hocPhanTienSi" className="font-weight-bold">Học phần chuyên sâu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="chuyenDeTienSi" className="font-weight-bold">Chuyên đề tiến sĩ</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="tieuLuanTongQuan" className="font-weight-bold">Tiểu luận tổng quan</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Button variant="success" size="sm" onClick={() => setViewMode('add')}>
                        <i className="fas fa-plus mr-1"></i> Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th>Loại học phần</th>
                                    <th>Tên học phần</th>
                                    <th className="text-center">File ký ban hành</th>
                                    <th className="text-center">Từ ngày</th>
                                    <th className="text-center">Đến ngày</th>
                                    <th className="text-center">Trạng thái</th>
                                </tr>
                            </thead>
                        <tbody>
                            {dataList.length > 0 ? (
                                dataList.map((item, index) => (
                                    <tr 
                                        key={item.id}
                                        onClick={() => setSelectedRow(item)}
                                        style={{ cursor: 'pointer' }}
                                        className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                    >
                                        <td className="text-center">{index + 1}</td>
                                        <td>{getLoaiHocPhanLabel(item.loaiHocPhan)}</td>
                                        <td>{item.tenHocPhan}</td>
                                        <td className="text-center">
                                            {item.fileBanHanh ? (
                                                (() => {
                                                    const path = item.fileBanHanh;
                                                    const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                    const safePath = path.startsWith('/') ? path : `/${path}`;
                                                    const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                    let name = path.split('/').pop() || 'File';
                                                    name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                    return (
                                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                            <i className="fas fa-paperclip mr-1"></i> {name}
                                                        </a>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">Không có</span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {item.tuNgay ? moment(item.tuNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">
                                            {item.denNgay ? moment(item.denNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        Chưa có dữ liệu Học phần tiến sĩ
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    </div>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="warning" 
                            size="sm" 
                            onClick={() => handleAction(selectedRow)} 
                            className="mr-2 text-white"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-tasks mr-1"></i> Cập nhật trạng thái
                        </Button>
                        <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => { setDataEdit(selectedRow); setViewMode('edit'); }} 
                            className="mr-2"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                        <Button 
                            variant="primary" 
                            size="sm" 
                            onClick={() => { setDataEdit(selectedRow); setViewMode('chamDeCuong'); }} 
                            className="mr-2"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-check-double mr-1"></i> Chấm đề cương
                        </Button>
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => deleteAction(selectedRow?.id, selectedRow?.tenHocPhan)}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-trash mr-1"></i> Xóa
                        </Button>
                    </div>
                </Card.Footer>
            )}
        </Card>

        <ConfirmModal
            isOpen={openConfirmDeleteModal}
            confirmType="delete"
            goBackAfterDelete={false}
            handleAccept={acceptDelete}
            handleCancel={cancelDelete}
            content={confirmDelMessage}
        />

        {showModal && (
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} size="md">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Cập nhật trạng thái Học phần tiến sĩ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Trạng thái</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={trangThai}
                                onChange={(e) => setTrangThai(e.target.value)}
                            >
                                {objectTrangThaiHocPhan.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={submitDuyet} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        
        {viewMode === 'edit' && dataEdit && (
            <HocPhanTienSiEdit 
                hoSoId={hoSoId} 
                dataEdit={dataEdit} 
                setViewMode={setViewMode} 
                refreshList={refetch} 
                isView={false} 
                isUserView={false} 
            />
        )}
        {viewMode === 'chamDeCuong' && dataEdit && (
            <ChamDeCuongModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        {viewMode === 'add' && (
            <HocPhanTienSiAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />
        )}
        </>
    );
};

export default DuyetHocPhanTienSi;
